import { APP_BUILD_ID, VERSION_CHECK_INTERVAL_MS } from "../../constants/env";
import { tryAcquireReloadPermit } from "../app-reload/reload-permit";
import { reloadWindow } from "../app-reload/reload-window";
import { fetchRemoteBuildId } from "./fetch-remote-build-id";

// Tolerance so the interval tick itself is never rejected by the throttle
const THROTTLE_TOLERANCE_MS = 1_000;

let lastCheckAt = 0;
let isCheckInFlight = false;

function isDetectionEnabled(): boolean {
  if (process.env.NODE_ENV !== "production") {
    return false;
  }

  const hasRealBuildId = APP_BUILD_ID !== "unknown" && !APP_BUILD_ID.startsWith("local-");
  return hasRealBuildId;
}

async function checkForNewBuild(): Promise<void> {
  const isThrottled = Date.now() - lastCheckAt < VERSION_CHECK_INTERVAL_MS - THROTTLE_TOLERANCE_MS;
  if (isThrottled || isCheckInFlight) {
    return;
  }

  lastCheckAt = Date.now();
  isCheckInFlight = true;
  const remoteBuildId = await fetchRemoteBuildId();
  isCheckInFlight = false;

  const isNewBuildDeployed = remoteBuildId !== undefined && remoteBuildId !== APP_BUILD_ID;
  if (!isNewBuildDeployed) {
    return;
  }

  const hasReloadPermit = tryAcquireReloadPermit();
  if (hasReloadPermit) {
    reloadWindow();
  }
}

export function startStaleBuildDetection(): () => void {
  if (!isDetectionEnabled()) {
    return function noop() {};
  }

  lastCheckAt = Date.now();
  const intervalId = window.setInterval(checkForNewBuild, VERSION_CHECK_INTERVAL_MS);

  function handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      void checkForNewBuild();
    }
  }

  function handlePageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      void checkForNewBuild();
    }
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pageshow", handlePageShow);

  return function stopStaleBuildDetection() {
    window.clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pageshow", handlePageShow);
  };
}
